import type { TableKitOptions, TableOptions } from '@tiptap/extension-table';
import type { TextStyleKitOptions } from '@tiptap/extension-text-style';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { StarterKitOptions } from '@tiptap/starter-kit';
import isNil from 'lodash/isNil';

import { RICH_TEXT_PROSE_CLASS_NAME } from '@ui/components/editor/richTextUtils';
import { LINE_BREAK_TAG, transformHtml, wrapTemplateTokens } from '@ui/components/editor/templateTagUtils';
import { cn } from '@ui/lib/utils';

export type TemplateTextAlignment = 'left' | 'center' | 'right' | 'justify';

export const TEMPLATE_TEXT_ALIGNMENTS: TemplateTextAlignment[] = ['left', 'center', 'right', 'justify'];

export type TemplateTableAlignment = Extract<TemplateTextAlignment, 'center' | 'right'>;

export type TemplateBlockType = 'paragraph' | 'heading1' | 'heading2' | 'heading3';

export const TEMPLATE_BLOCK_TYPES: TemplateBlockType[] = ['paragraph', 'heading1', 'heading2', 'heading3'];

export const TEMPLATE_FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

export interface TemplateContentColor {
  name: string;
  value: string;
}

export const TEMPLATE_TEXT_COLORS: TemplateContentColor[] = [
  { name: 'black', value: '#000000' },
  { name: 'darkGrey', value: '#434343' },
  { name: 'grey', value: '#757575' },
  { name: 'red', value: '#c62828' },
  { name: 'orange', value: '#e65100' },
  { name: 'yellow', value: '#b58900' },
  { name: 'green', value: '#2e7d32' },
  { name: 'teal', value: '#00796b' },
  { name: 'blue', value: '#1565c0' },
  { name: 'purple', value: '#6a1b9a' },
];

export const TEMPLATE_HIGHLIGHT_COLORS: TemplateContentColor[] = [
  { name: 'yellow', value: '#fff59d' },
  { name: 'orange', value: '#ffe0b2' },
  { name: 'red', value: '#ffcdd2' },
  { name: 'pink', value: '#f8bbd0' },
  { name: 'purple', value: '#e1bee7' },
  { name: 'blue', value: '#bbdefb' },
  { name: 'green', value: '#c8e6c9' },
  { name: 'grey', value: '#eeeeee' },
];

const NESTED_BLOCK_CONTAINERS = ['li', 'td', 'th', 'blockquote'];
const SELECTOR_SEPARATOR = ', ';
const PARAGRAPH_TAG = 'p';
const toNestedParagraphSelector = (container: string): string => `${container} > ${PARAGRAPH_TAG}`;
const NESTED_PARAGRAPH_SELECTOR = NESTED_BLOCK_CONTAINERS.map(toNestedParagraphSelector).join(SELECTOR_SEPARATOR);
const NESTED_PARAGRAPH_MARGIN = '0';
const EMPTY_PARAGRAPH_SELECTOR = 'p:empty';
const BLANK_LINE_SELECTOR = 'p, div';

export const NESTED_BLOCK_CONTAINER_SELECTOR = NESTED_BLOCK_CONTAINERS.join(SELECTOR_SEPARATOR);

const holdsOnlyALineBreak = (element: Element): boolean =>
  element.childNodes.length === 1 && element.firstChild instanceof HTMLBRElement;

export const writeEmailSafeHtml = <Target extends ParentNode>(target: Target): Target => {
  target.querySelectorAll<HTMLElement>(NESTED_PARAGRAPH_SELECTOR).forEach((paragraph) => {
    paragraph.style.setProperty('margin', NESTED_PARAGRAPH_MARGIN);
  });
  target.querySelectorAll(EMPTY_PARAGRAPH_SELECTOR).forEach((paragraph) => {
    paragraph.append(paragraph.ownerDocument.createElement(LINE_BREAK_TAG));
  });
  return target;
};

export const collapseBlankLines = (html: string): string =>
  transformHtml(html, (content) => {
    content.querySelectorAll(BLANK_LINE_SELECTOR).forEach((line) => {
      if (!holdsOnlyALineBreak(line)) {
        return;
      }
      if (line instanceof HTMLParagraphElement) {
        line.replaceChildren();
        return;
      }
      line.replaceWith(document.createElement(PARAGRAPH_TAG));
    });
  });

export const toTemplateEditorHtml = (html: string): string => wrapTemplateTokens(collapseBlankLines(html));

export const toComparableColor = (color: string): string => {
  const probe = document.createElement('span');
  probe.style.color = color;
  return probe.style.color;
};

export interface TemplateTableSize {
  rows: number;
  cols: number;
}

export const TEMPLATE_TABLE_SIZES: TemplateTableSize[] = [
  { rows: 2, cols: 2 },
  { rows: 3, cols: 3 },
  { rows: 4, cols: 4 },
];

export const TABLE_NODE_NAME = 'table';
export const TABLE_BODY_ELEMENT = 'tbody';
export const TABLE_ALIGN_DATA_ATTRIBUTE = 'data-align';

const TEMPLATE_TABLE_ALIGNMENTS: TemplateTableAlignment[] = ['center', 'right'];
const TABLE_ALIGN_ATTRIBUTE = 'align';
const AUTO_MARGIN = 'auto';

const TEMPLATE_TABLE_ALIGNMENT_ATTRIBUTES: Record<TemplateTableAlignment, Record<string, string>> = {
  center: { [TABLE_ALIGN_ATTRIBUTE]: 'center', style: 'margin-left: auto; margin-right: auto' },
  right: { style: 'margin-left: auto; margin-right: 0' },
};

export const toTemplateTableAlignment = (alignment: string | null): TemplateTableAlignment | null =>
  TEMPLATE_TABLE_ALIGNMENTS.find((tableAlignment) => tableAlignment === alignment) ?? null;

export const readTemplateTableAlignment = (table: ProseMirrorNode): TemplateTableAlignment | null =>
  toTemplateTableAlignment(table.attrs.align);

export const parseTemplateTableAlignment = (element: HTMLElement): TemplateTableAlignment | null => {
  const alignment = toTemplateTableAlignment(element.getAttribute(TABLE_ALIGN_ATTRIBUTE)?.toLowerCase() ?? null);
  if (!isNil(alignment)) {
    return alignment;
  }
  const { marginLeft, marginRight } = element.style;
  if (marginLeft !== AUTO_MARGIN) {
    return null;
  }
  return marginRight === AUTO_MARGIN ? 'center' : 'right';
};

export const renderTemplateTableAlignment = (alignment: TemplateTableAlignment | null): Record<string, string> =>
  isNil(alignment) ? {} : TEMPLATE_TABLE_ALIGNMENT_ATTRIBUTES[alignment];

const TEMPLATE_BORDER_COLOR = '#c4ccd4';
const TEMPLATE_HEADER_CELL_BACKGROUND = '#f2f4f6';
const TEMPLATE_QUOTE_TEXT_COLOR = '#555f6b';
const TEMPLATE_CELL_PADDING = '6';

const TEMPLATE_TABLE_ATTRIBUTES = {
  border: '1',
  cellpadding: TEMPLATE_CELL_PADDING,
  cellspacing: '0',
  style: 'border-collapse: collapse',
};

const TEMPLATE_CELL_STYLE = [
  `border: 1px solid ${TEMPLATE_BORDER_COLOR}`,
  `padding: ${TEMPLATE_CELL_PADDING}px`,
  'vertical-align: top',
].join('; ');

const TEMPLATE_TABLE_CELL_ATTRIBUTES = { style: TEMPLATE_CELL_STYLE };

const TEMPLATE_TABLE_HEADER_ATTRIBUTES = {
  style: `${TEMPLATE_CELL_STYLE}; background-color: ${TEMPLATE_HEADER_CELL_BACKGROUND}; text-align: left`,
};

export const TEMPLATE_IMAGE_ATTRIBUTES = { style: 'max-width: 100%; height: auto' };

const TEMPLATE_BLOCKQUOTE_ATTRIBUTES = {
  style: [
    'margin: 0',
    'padding-left: 12px',
    `border-left: 3px solid ${TEMPLATE_BORDER_COLOR}`,
    `color: ${TEMPLATE_QUOTE_TEXT_COLOR}`,
  ].join('; '),
};

const TEMPLATE_LINK_DEFAULT_PROTOCOL = 'https';

export const TEMPLATE_TEXT_STARTER_KIT_OPTIONS: Partial<StarterKitOptions> = {
  heading: { levels: [1, 2, 3] },
  code: false,
  codeBlock: false,
  underline: false,
  blockquote: { HTMLAttributes: TEMPLATE_BLOCKQUOTE_ATTRIBUTES },
  link: { openOnClick: false, autolink: true, linkOnPaste: true, defaultProtocol: TEMPLATE_LINK_DEFAULT_PROTOCOL },
};

export const TEMPLATE_TEXT_TABLE_OPTIONS: Partial<TableOptions> = {
  resizable: false,
  HTMLAttributes: TEMPLATE_TABLE_ATTRIBUTES,
};

export const TEMPLATE_TEXT_TABLE_KIT_OPTIONS: Partial<TableKitOptions> = {
  table: false,
  tableCell: { HTMLAttributes: TEMPLATE_TABLE_CELL_ATTRIBUTES },
  tableHeader: { HTMLAttributes: TEMPLATE_TABLE_HEADER_ATTRIBUTES },
};

export const TEMPLATE_TEXT_STYLE_KIT_OPTIONS: Partial<TextStyleKitOptions> = {
  fontFamily: false,
  lineHeight: false,
};

export const TEMPLATE_INVALID_SURFACE_CLASS_NAME = cn(
  'data-[invalid=true]:border-destructive data-[invalid=true]:ring-3 data-[invalid=true]:ring-destructive/20',
  'dark:data-[invalid=true]:border-destructive/50 dark:data-[invalid=true]:ring-destructive/40'
);

export const TEMPLATE_PLACEHOLDER_CLASS_NAME = cn(
  '[&_.is-editor-empty:first-child]:before:pointer-events-none [&_.is-editor-empty:first-child]:before:float-left',
  '[&_.is-editor-empty:first-child]:before:h-0 [&_.is-editor-empty:first-child]:before:text-muted-foreground',
  '[&_.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]'
);

export const TEMPLATE_TEXT_PROSE_CLASS_NAME = cn(
  RICH_TEXT_PROSE_CLASS_NAME,
  '[&_u]:underline',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul_ul]:list-[circle]',
  '[&_img]:inline-block [&_img]:w-auto [&_img]:max-w-full [&_img]:rounded-none',
  '[&_table[data-align]]:w-fit',
  '[&_hr]:my-4 [&_hr]:border-border',
  '[&_.selectedCell]:relative [&_.selectedCell]:after:pointer-events-none [&_.selectedCell]:after:absolute',
  "[&_.selectedCell]:after:inset-0 [&_.selectedCell]:after:bg-primary/15 [&_.selectedCell]:after:content-['']",
  TEMPLATE_PLACEHOLDER_CLASS_NAME
);
